import {
  Box,
  createStyles,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Theme,
  Typography,
} from '@material-ui/core';

import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Close } from '@material-ui/icons';
import PhoneTextField from '../../../phone-text-field';
import EmailTextField from '../../../email-text-field';
import TextField from '../../../text-field';
import { TextAreaInput } from '../../../pge-plus-common/text-area-input';
import Buttons from '../../../buttons';
import colors from '../../../../themes/main-colors';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import useFormState from '../../../../hooks/useFormState';
import validationRules, { Model } from './GetInTouchModal.rules';
import { useTranslation } from '../../../../hooks/useTranslation';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Dropdown from '../../../dropdown';
import gql from 'not-graphql-tag';
import useUtil from '../../../../hooks/pge-plus/useUtil';
import { useApolloClient } from '@apollo/react-hooks';
import { NotificationsContext } from '../../../../providers/NotificationsProvider';
import { splitName } from '../../../../util/string-utils';
import useAccountCustomer from '../../../../hooks/useAccountCustomer';
import { parsePhoneNumber } from '../../../../util/format';
import useAuthQuery from '../../../../hooks/useAuthQuery';
import { AccountDetail } from '../../../../__generated__/pge-types';
import useSelectedAccountParams from '../../../../hooks/useSelectedAccountParams';
import microcopyGroupIds from '../../../pge-plus-common/microcopyGroupIds';
import useContentMgmt, {
  PageTextContentType,
} from '../../../../hooks/useContentMgmt';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    closeBtn: {
      position: 'absolute',
      top: 12,
      right: 12,
      padding: 0,
    },
    popupContent: {
      padding: '0px',
    },
    popupContentArea: {
      padding: theme.spacing(0, 3, 3, 3),
      [theme.breakpoints.down('sm')]: {
        padding: `${theme.spacing(2, 4)} !important`,
      },
    },
    paper: {
      [theme.breakpoints.down('xs')]: {
        position: 'relative',
        width: 300,
        backgroundColor: theme.palette.background.paper,
        border: '0px solid #FFF',
        boxShadow: 'none',
        padding: theme.spacing(2),
        outline: 0,
      },
      [theme.breakpoints.up('md')]: {
        position: 'relative',
        width: 400,
        backgroundColor: theme.palette.background.paper,
        border: '0px solid #FFF',
        boxShadow: 'none',
        outline: 0,
      },
    },
    textField: {
      width: '100%',
      margin: '8px 0 8px 0',
      padding: '0',
    },
    title: {
      margin: '1rem 0px 1rem 0rem',
      textAlign: 'center',
    },
    greenCheck: {
      fill: 'green',
      fontSize: '2rem',
    },
    errorMessage: {
      margin: '0 14px',
    },
    box: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      padding: 20,
      fontWeight: 600,
      margin: '2rem 1.5rem',
    },
  }),
);

interface Props {
  open: boolean;
  onClose: () => void;
  applicationId: string | React.ReactNode | undefined;
  reasonSelected?: boolean;
}

export const defaultFormValues: Model = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  emailAddress: '',
  alternateEmail: '',
  reason: {
    value: '',
    label: '',
  },
  comments: '',
};

export const CustomerInitiatedSupportMutation = gql`
  mutation CreateCustomerInitiatedSupportRequest(
    $input: CreateCustomerInitiatedSupportInput!
  ) {
    createCustomerInitiatedSupportRequest(input: $input) {
      success
    }
  }
`;

export const getAccountDetailsQuery = gql`
  query getAccountDetails($params: AccountDetailParams!) {
    getAccountDetails(params: $params) {
      accountNumber
      relationType
    }
  }
`;

const GetInTouchModal = ({
  open,
  onClose,
  applicationId,
  reasonSelected,
}: Props) => {
  const classes = useStyles();
  const { wrapWithLoader } = useWrapWithLoader();
  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_WELCOME_BACK_GET_SUPPORT,
  );

  useEffect(() => {
    if (open) {
      setComplete(false);
    }
  }, [open]);

  const apolloClient = useApolloClient();
  const form = useFormState(defaultFormValues, {
    validate: validationRules,
    validationContext: {
      content,
    },
  });

  const [isComplete, setComplete] = useState(false);
  const { setErrorNotification } = useUtil();

  async function submitGetSupport(
    data: Model,
  ): Promise<{
    success: boolean;
  }> {
    const queryResult = await apolloClient.mutate({
      mutation: CustomerInitiatedSupportMutation,
      variables: {
        input: {
          alternateEmail: data.alternateEmail,
          applicationId: applicationId,
          comments: data.comments,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phoneNumber,
          reason: data.reason,
        },
      },
      context: { pgePlus: true },
    });
    return queryResult?.data?.createCustomerInitiatedSupportRequest;
  }

  const handleGetSupportFormSubmit = wrapWithLoader(
    async (data: Model): Promise<void> => {
      try {
        const createCustomerInitiatedSupportRequest = await submitGetSupport(
          data,
        );
        const supportRequest = createCustomerInitiatedSupportRequest;

        if (supportRequest?.success) {
          setComplete(true);
        } else {
          setErrorNotification(true);
          if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
          }
        }
      } catch (e) {
        setErrorNotification(true);
        return;
      }
    },
  );
  const handleSubmit = form.submit(handleGetSupportFormSubmit);

  const { customer, loading: loadingCustomer } = useAccountCustomer();

  const { accountParams } = useSelectedAccountParams();
  const { loading: hookLoading, data: accountDetails } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getAccountDetailsQuery, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    skip: !accountParams,
  });

  const CustomerSplitName = splitName(customer?.personName || '');
  const firstN = CustomerSplitName?.firstName;
  const lastN = CustomerSplitName?.lastName;

  const contactDetail = customer?.contactDetails;
  const pnpPhoneData =
    contactDetail && contactDetail.find(pnp => pnp?.contactType === 'PNP');
  const pnpPhone = pnpPhoneData?.contactValue
    ? parsePhoneNumber(pnpPhoneData?.contactValue)
    : '';

  const selectedAccount = accountDetails?.getAccountDetails?.[0];
  useEffect(() => {
    void form.setValue('firstName', firstN || '');
    void form.setValue('lastName', lastN || '');
    void form.setValue('emailAddress', customer?.email);
    void form.setValue('phoneNumber', pnpPhone ? pnpPhone : '');
    if (reasonSelected) {
      void form.setValue(
        'reason',
        content.get('INSTALLER_HAS_NOT_YET_CONTACTED_ME'),
      );
    } else {
      void form.setValue('reason', '');
    }
    void form.setValue('comments', '');
  }, [selectedAccount, customer, open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          onClose;
        }}
        onBackdropClick={onClose}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        scroll="body"
        disableScrollLock
      >
        {isComplete ? (
          <GetInTouchSuccessForm content={content} />
        ) : (
          <>
            <DialogTitle style={{ padding: '20px 24px 0px 24px' }}>
              <Grid className={'outageTitle'} container justify="space-between">
                <Typography variant={'h2'} noWrap={true} gutterBottom>
                  {content.get('GET_IN_TOUCH_TITLE')}
                </Typography>
                <IconButton
                  aria-label="close"
                  color="inherit"
                  onClick={onClose}
                  className={classes.closeBtn}
                >
                  <Close />
                </IconButton>
              </Grid>
            </DialogTitle>
            <DialogContent className={classes.popupContent}>
              <Paper elevation={0} className={classes.popupContentArea}>
                <Typography variant="body1">
                  {content.get('GET_IN_TOUCH_TEXT')}
                </Typography>
                <form noValidate onSubmit={handleSubmit}>
                  <Grid
                    container
                    direction="column"
                    css={{ marginTop: '16px' }}
                  >
                    <Grid container item spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          className={classes.textField}
                          name={'firstName'}
                          label={`${content.get('FIRST_NAME')} ${'*'}`}
                          {...form.props('firstName')}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          className={classes.textField}
                          name={'lastName'}
                          label={`${content.get('LAST_NAME')} ${'*'}`}
                          {...form.props('lastName')}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container direction="column">
                    <Grid container item spacing={2}>
                      <Grid item xs={12} md={6}>
                        <PhoneTextField
                          className={classes.textField}
                          name={'phoneNumber'}
                          label={`${content.get('PHONE_NUMBER')} ${'*'}`}
                          {...form.props('phoneNumber')}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <EmailTextField
                          className={classes.textField}
                          disabled
                          name={'emailAddress'}
                          label={`${content.get('REUSABLE_EMAIL')} ${'*'}`}
                          {...form.props('emailAddress')}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    direction="column"
                    css={{ padding: '16px 8px' }}
                  >
                    <Grid container item spacing={2}>
                      <Typography variant="body1">
                        {content.get('ADDITIONAL_EMAIL_ADDRESS')}
                      </Typography>
                      <EmailTextField
                        className={classes.textField}
                        name={'alternateEmail'}
                        label={content.get('ALTERNATE_EMAIL_ADDRESS')}
                        {...form.props('alternateEmail')}
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={12}
                    css={{ paddingTop: 5, paddingBottom: 5 }}
                  >
                    <Dropdown
                      name={'reason'}
                      label={`${content.get(
                        'SELECT_TOPIC_OF_YOUR_CONCERN',
                      )} ${'*'}`}
                      data-testid="reason"
                      selectionList={[
                        {
                          value: content.get(
                            'INSTALLER_HAS_NOT_YET_CONTACTED_ME',
                          ),
                          label: content.get(
                            'INSTALLER_HAS_NOT_YET_CONTACTED_ME',
                          ),
                        },
                        {
                          value: content.get('INSTALLER_ISSUE'),
                          label: content.get('INSTALLER_ISSUE'),
                        },
                        {
                          value: content.get('QUOTE_ISSUE'),
                          label: content.get('QUOTE_ISSUE'),
                        },
                        {
                          value: content.get('SUPPORT_OPTION_OTHER'),
                          label: content.get('SUPPORT_OPTION_OTHER'),
                        },
                      ]}
                      {...form.props('reason')}
                      onChange={(e: any) => {
                        void form.setValue('reason', e.target.value);
                        void form.onChange(e);
                      }}
                      error={Boolean(form.errors.reason)}
                    />

                    {form.errors.reason && (
                      <FormHelperText
                        className={classes.errorMessage}
                        error={true}
                      >
                        {form.errors.reason}
                      </FormHelperText>
                    )}
                  </Grid>
                  <Grid
                    container
                    direction="column"
                    css={{ padding: '16px 8px' }}
                  >
                    <Grid container item spacing={2}>
                      <Typography variant="body1">
                        {content.get('ADDITIONAL_COMMENTS ')}
                      </Typography>
                      <TextAreaInput
                        {...form.props('comments')}
                        value={form.values.comments as string}
                        placeholder={content.get('COMMENTS')}
                      />
                      {form.errors.comments && (
                        <FormHelperText
                          className={classes.errorMessage}
                          error={true}
                        >
                          {form.errors.comments}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    spacing={2}
                    css={{
                      marginTop: 24,
                      marginBottom: 24,
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '12px',
                    }}
                  >
                    <Buttons
                      type="button"
                      variant="outlined"
                      color="secondary"
                      onClick={onClose}
                      css={{
                        fontSize: '18px',
                        borderRadius: '30px',
                        padding: '4px 32px',
                        '.MuiButton-label': {
                          fontWeight: 500,
                        },
                        '&:hover': {
                          borderWidth: '2px',
                        },
                      }}
                    >
                      {content.get('REUSABLE_CANCEL')}
                    </Buttons>
                    <Buttons
                      type="submit"
                      variant="contained"
                      onClick={handleSubmit}
                      css={{
                        fontSize: '18px',
                        padding: '4px 60px',
                        background: colors.orange,
                        borderRadius: '30px',
                        '.MuiButton-label': {
                          fontWeight: 500,
                        },
                        '&:hover': {
                          background: colors.orange,
                        },
                      }}
                    >
                      {content.get('REUSABLE_SEND')}
                    </Buttons>
                  </Grid>
                </form>
              </Paper>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};
interface PageTextContentProps {
  content: PageTextContentType;
}

const GetInTouchSuccessForm: React.FC<PageTextContentProps> = ({ content }) => {
  const classes = useStyles();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Fragment>
      <Paper css={{ marginTop: 24, boxShadow: 'none' }}>
        <Box className={classes.box}>
          <CheckCircleIcon className={classes.greenCheck} />
          <Typography variant="h2" className={classes.title}>
            {content.get('GET_SUPPORT_SUCCESS')}
          </Typography>
          <Typography variant="body1" css={{ margin: '.5rem 0px 1rem 0rem' }}>
            {content.get('GET_SUPPORT_SUCCESS_TEXT')}
          </Typography>
        </Box>
      </Paper>
    </Fragment>
  );
};

export default GetInTouchModal;
